import { CLIP_ASSET_BYTES, IDLE_ASSET_IDS } from '../data/characterClips'
import { LOAD_ALL_UPFRONT } from './sceneLoadConfig'

export const SCENE_LOAD_PHASE = {
  init: 'init',
  phase1: 'phase-1',
  phase1Done: 'phase-1-done',
  phase2Wait: 'phase-2-wait',
  phase2: 'phase-2',
  complete: 'complete',
}

const ASSET_STATUS = {
  pending: 'pending',
  loading: 'loading',
  done: 'done',
  error: 'error',
  skipped: 'skipped',
}

const createInitialState = () => ({
  phase: SCENE_LOAD_PHASE.init,
  loadAllUpfront: LOAD_ALL_UPFRONT,
  assets: {},
  phase2AssetIds: [],
  phase2Labels: {},
  wantedAssetIds: [],
  timestamps: {
    sessionStart: performance.now(),
    phase1Start: null,
    phase1Done: null,
    phase2Wait: null,
    phase2Start: null,
    phase2Done: null,
    firstWebglPaint: null,
  },
})

let state = createInitialState()
const listeners = new Set()
const assetPromises = new Map()

const emit = () => {
  listeners.forEach((listener) => listener(state))
}

const stamp = () => performance.now()

export const subscribeSceneLoadTracker = (listener) => {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

export const getSceneLoadState = () => state

export const resetSceneLoadTracker = () => {
  state = createInitialState()
  emit()
}

export const markPhase1Start = () => {
  if (state.timestamps.phase1Start != null) return
  state = {
    ...state,
    phase: SCENE_LOAD_PHASE.phase1,
    timestamps: { ...state.timestamps, phase1Start: stamp() },
  }
  emit()
}

export const markPhase1Done = () => {
  if (state.timestamps.phase1Done != null) return
  state = {
    ...state,
    phase: SCENE_LOAD_PHASE.phase1Done,
    timestamps: { ...state.timestamps, phase1Done: stamp() },
  }
  emit()
}

export const markPhase2Wait = () => {
  if (state.timestamps.phase2Wait != null) return
  state = {
    ...state,
    phase: SCENE_LOAD_PHASE.phase2Wait,
    timestamps: { ...state.timestamps, phase2Wait: stamp() },
  }
  emit()
}

export const markPhase2Start = () => {
  if (state.timestamps.phase2Start != null) return
  state = {
    ...state,
    phase: SCENE_LOAD_PHASE.phase2,
    timestamps: { ...state.timestamps, phase2Start: stamp() },
  }
  emit()
}

export const markPhase2Done = () => {
  if (state.timestamps.phase2Done != null) return
  state = {
    ...state,
    phase: SCENE_LOAD_PHASE.complete,
    timestamps: { ...state.timestamps, phase2Done: stamp() },
  }
  emit()
}

export const markPhase2Skipped = (reason = 'upfront') => {
  state = {
    ...state,
    phase: SCENE_LOAD_PHASE.complete,
    timestamps: {
      ...state.timestamps,
      phase2Wait: state.timestamps.phase2Wait ?? stamp(),
      phase2Start: state.timestamps.phase2Start ?? stamp(),
      phase2Done: stamp(),
    },
    skipReason: reason,
  }
  emit()
}

export const markFirstWebglPaint = (at = stamp()) => {
  if (state.timestamps.firstWebglPaint != null) return
  state = {
    ...state,
    timestamps: { ...state.timestamps, firstWebglPaint: at },
  }
  emit()
}

const registerAsset = (id, label, status = ASSET_STATUS.pending) => {
  if (state.assets[id]) return
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: {
        id,
        label,
        status,
        phase: state.timestamps.phase1Done != null ? 2 : 1,
        startedAt: null,
        doneAt: null,
        bytesLoaded: 0,
        bytesTotal: 0,
      },
    },
  }
  emit()
}

const markAssetLoading = (id, label, phase) => {
  const prev = state.assets[id]
  const nextPhase =
    prev?.phase
    ?? phase
    ?? (state.timestamps.phase1Done != null ? 2 : 1)
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: {
        id,
        label: label || prev?.label || id,
        status: ASSET_STATUS.loading,
        phase: nextPhase,
        startedAt: prev?.startedAt ?? stamp(),
        doneAt: null,
        bytesLoaded: prev?.bytesLoaded ?? 0,
        bytesTotal: prev?.bytesTotal ?? 0,
      },
    },
  }
  emit()
}

/** Byte sizes from `public/models` — fallback when Content-Length is missing. */
export const PHASE1_ASSET_IDS = ['sakira-mesh', 'song-mesh', ...IDLE_ASSET_IDS]

const PHASE1_MESH_IDS = new Set(['sakira-mesh', 'song-mesh'])

const NON_CLIP_BYTES = {
  'sakira-mesh': 2781416,
  'song-mesh': 3899832,
  'sakira-rig': 186505,
  'song-rig': 294653,
  'anim-8-preview': 3761114,
  'sakira-mods-preview': 1885584,
}

export const ASSET_BYTES = { ...NON_CLIP_BYTES, ...CLIP_ASSET_BYTES }

export const markAssetProgress = (id, loaded, total) => {
  const prev = state.assets[id]
  if (!prev) return
  const nextLoaded = Math.max(0, Number(loaded) || 0)
  let nextTotal = Number(total) || 0
  if (nextTotal <= 0) {
    nextTotal = prev.bytesTotal > 0 ? prev.bytesTotal : (ASSET_BYTES[id] || 0)
  }
  if (
    prev.bytesLoaded === nextLoaded
    && prev.bytesTotal === nextTotal
  ) {
    return
  }
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: {
        ...prev,
        bytesLoaded: nextLoaded,
        bytesTotal: nextTotal,
      },
    },
  }
  emit()
}

const getPhase1Candidates = () =>
  PHASE1_ASSET_IDS.map((id) => {
    if (state.assets[id]) return state.assets[id]
    return {
      id,
      label: id,
      status: ASSET_STATUS.pending,
      phase: 1,
      bytesLoaded: 0,
      bytesTotal: ASSET_BYTES[id] || 0,
    }
  })

export const getPhase1Progress = () => {
  const phase1Assets = getPhase1Candidates()
  if (!phase1Assets.length) return 0

  let loadedSum = 0
  let totalSum = 0
  for (const asset of phase1Assets) {
    const fallback = ASSET_BYTES[asset.id] || 0
    const total =
      asset.bytesTotal > 0
        ? asset.bytesTotal
        : fallback > 0
          ? fallback
          : 1
    const settled =
      asset.status === ASSET_STATUS.done
      || asset.status === ASSET_STATUS.error
      || asset.status === ASSET_STATUS.skipped
    const loaded = settled
      ? total
      : Math.min(Math.max(0, asset.bytesLoaded || 0), total)
    loadedSum += loaded
    totalSum += total
  }
  if (totalSum <= 0) return 0
  return Math.min(1, loadedSum / totalSum)
}

/**
 * Welcome loading line status from real tracker bytes (not timers).
 * scene → characters → decoding → done
 */
export const getPhase1Status = () => {
  if (state.timestamps.phase1Done != null) return 'done'
  if (getPhase1Progress() >= 1) return 'decoding'

  const phase1Assets = getPhase1Candidates()
  const meshAssets = phase1Assets.filter((asset) => PHASE1_MESH_IDS.has(asset.id))
  const anyMeshInFlight = meshAssets.some((asset) => !isAssetSettled(asset))
  if (!anyMeshInFlight) return 'decoding'

  const anyBytes = phase1Assets.some(
    (asset) =>
      (asset.bytesLoaded || 0) > 0
      || asset.status === ASSET_STATUS.done
      || asset.status === ASSET_STATUS.error
      || asset.status === ASSET_STATUS.skipped
  )
  if (anyBytes) return 'characters'
  return 'scene'
}

const markAssetDone = (id) => {
  const prev = state.assets[id]
  if (!prev) return
  const fallback = ASSET_BYTES[id] || 0
  const bytesTotal =
    prev.bytesTotal > 0
      ? prev.bytesTotal
      : fallback > 0
        ? fallback
        : prev.bytesLoaded || 0
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: {
        ...prev,
        status: ASSET_STATUS.done,
        doneAt: stamp(),
        bytesLoaded: bytesTotal,
        bytesTotal,
      },
    },
  }
  emit()
}

const markAssetError = (id) => {
  const prev = state.assets[id]
  if (!prev) return
  const fallback = ASSET_BYTES[id] || 0
  const bytesTotal =
    prev.bytesTotal > 0
      ? prev.bytesTotal
      : fallback > 0
        ? fallback
        : prev.bytesLoaded || 0
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: {
        ...prev,
        status: ASSET_STATUS.error,
        doneAt: stamp(),
        bytesLoaded: bytesTotal,
        bytesTotal,
      },
    },
  }
  emit()
}

export const markAssetSkipped = (id, label) => {
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: {
        id,
        label: label || state.assets[id]?.label || id,
        status: ASSET_STATUS.skipped,
        startedAt: null,
        doneAt: stamp(),
      },
    },
  }
  emit()
}

export const suffixAssetLabel = (id, suffix) => {
  const prev = state.assets[id]
  if (!prev || !suffix) return
  if (prev.label.includes(suffix)) return
  state = {
    ...state,
    assets: {
      ...state.assets,
      [id]: { ...prev, label: `${prev.label} ${suffix}` },
    },
  }
  emit()
}

export const formatLoadElapsed = (timestamp) => {
  if (timestamp == null) return '—'
  const origin = state.timestamps.sessionStart ?? timestamp
  return `${((timestamp - origin) / 1000).toFixed(2)}s`
}

const isAssetSettled = (asset) =>
  Boolean(
    asset
    && (
      asset.status === ASSET_STATUS.done
      || asset.status === ASSET_STATUS.error
      || asset.status === ASSET_STATUS.skipped
    )
  )

export const recordPhase2AssetIds = (entries = []) => {
  const unique = []
  const phase2Labels = {}
  for (const entry of entries || []) {
    const id = typeof entry === 'string' ? entry : entry?.id
    if (!id || unique.includes(id)) continue
    unique.push(id)
    phase2Labels[id] = typeof entry === 'string' ? id : (entry.label || id)
  }
  state = { ...state, phase2AssetIds: unique, phase2Labels }
  emit()
}

export const requestAsset = (id, label, loaderFn) => {
  const existing = assetPromises.get(id)
  if (existing) return existing

  const phase = state.timestamps.phase1Done != null ? 2 : 1
  if (!state.assets[id]) registerAsset(id, label)
  markAssetLoading(id, label, phase)

  const promise = Promise.resolve()
    .then(() => loaderFn())
    .then((result) => {
      markAssetDone(id)
      return result
    })
    .catch((error) => {
      markAssetError(id)
      assetPromises.delete(id)
      throw error
    })

  assetPromises.set(id, promise)
  return promise
}

export const whenSettled = (ids = []) => {
  const unique = [...new Set((ids || []).filter(Boolean))]
  if (!unique.length) return Promise.resolve()

  return new Promise((resolve) => {
    const check = () => unique.every((id) => isAssetSettled(state.assets[id]))
    if (check()) {
      resolve()
      return
    }
    const unsubscribe = subscribeSceneLoadTracker(() => {
      if (!check()) return
      unsubscribe()
      resolve()
    })
  })
}

export const ASSET_IDS = {
  sakiraMesh: 'sakira-mesh',
  songMesh: 'song-mesh',
  sakiraRig: 'sakira-rig',
  songRig: 'song-rig',
  sakiraShaders: 'shaders-sakira',
  songShaders: 'shaders-song',
  anim8Preview: 'anim-8-preview',
  sakiraPreview: 'sakira-mods-preview',
}

export const getAssetStatus = (id) => state.assets[id]?.status ?? null

export const setSceneWantedAssets = (ids = []) => {
  const unique = [...new Set((ids || []).filter(Boolean))]
  const prev = state.wantedAssetIds
  if (
    unique.length === prev.length
    && unique.every((id, index) => id === prev[index])
  ) {
    return
  }
  state = { ...state, wantedAssetIds: unique }
  emit()
}

export const getSceneWantedAssets = () => state.wantedAssetIds

export const resetAssetRegistry = () => {
  assetPromises.clear()
}

const SYNTHETIC_PHASE2_BYTES = 200000

const labelForPhase2Asset = (asset) => asset.label || asset.id

const getPhase2Candidates = () => {
  const phase2Ids = state.phase2AssetIds
  if (!phase2Ids.length) {
    return Object.values(state.assets).filter((asset) => asset.phase === 2)
  }
  return phase2Ids.map((id) => {
    if (state.assets[id]) return state.assets[id]
    return {
      id,
      label: state.phase2Labels[id] || id,
      status: ASSET_STATUS.pending,
      bytesLoaded: 0,
      bytesTotal: ASSET_BYTES[id] || 0,
    }
  })
}

export const getPhase2Progress = () => {
  const candidates = getPhase2Candidates()
  const currentAsset =
    candidates.find((asset) => asset.status === ASSET_STATUS.loading)
    || candidates.find((asset) => asset.status === ASSET_STATUS.pending)
  const current = currentAsset
    ? { id: currentAsset.id, label: labelForPhase2Asset(currentAsset) }
    : null

  let loadedSum = 0
  let totalSum = 0
  let done = 0
  for (const asset of candidates) {
    if (asset.status === ASSET_STATUS.done) done += 1
    const catalog = ASSET_BYTES[asset.id] || 0
    const total =
      catalog > 0
        ? catalog
        : asset.bytesTotal > 0
          ? asset.bytesTotal
          : SYNTHETIC_PHASE2_BYTES
    const settled =
      asset.status === ASSET_STATUS.done
      || asset.status === ASSET_STATUS.error
      || asset.status === ASSET_STATUS.skipped
    const loaded = settled
      ? total
      : Math.min(Math.max(0, asset.bytesLoaded || 0), total)
    loadedSum += loaded
    totalSum += total
  }

  return {
    current,
    fraction: totalSum > 0 ? Math.min(1, loadedSum / totalSum) : 0,
    done,
    total: candidates.length,
  }
}

export const getUnsettledPhase2Assets = () =>
  getPhase2Candidates().filter(
    (asset) =>
      asset.status === ASSET_STATUS.pending || asset.status === ASSET_STATUS.loading
  )
